import * as React from 'react'

import type {
  TableFieldDef,
  TableFields,
} from '~/client/src/desktop/tables/tableFields'
import { buildTableFields } from '~/client/src/desktop/tables/tableFields'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import { WorkflowTypeCreatedByEnum } from '~/client/src/shared/enums/WorkflowTypeCreatedBy'
import { WorkflowTypeGroupingOption } from '~/client/src/shared/enums/WorkflowTypeGroupingOption'
import {
  EMPTY_OPTION,
  normalizeUnassigned,
} from '~/client/src/shared/lists/core'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import ColumnHeaderInfo from './compound/ColumnHeaderInfo'
import ShowInAppCell from './compound/ShowInAppCell'
import {
  APP_FACET_KEY,
  AUDIENCE_ADMIN_ONLY,
  AUDIENCE_EVERYONE,
  FormTypeColumnId,
  HIDDEN_BAND_KEY,
  IFormTypeRow,
  SHOWN_BAND_KEY,
} from './types'

export interface FormTypeRowActionHandlers {
  onEdit: (formTypeId: string) => void
  onShowInApp: (formTypeId: string) => void
}

interface FormTypeFieldsArgs {
  readonly categoryOrder: readonly string[]
  readonly rowActionHandlers: FormTypeRowActionHandlers
}

export const FORM_TYPE_SAVED_VIEW_ALIASES: Readonly<Record<string, string>> = {
  [WorkflowTypeGroupingOption.CATEGORY]: FormTypeColumnId.Category,
  [WorkflowTypeGroupingOption.APP]: APP_FACET_KEY,
  [WorkflowTypeGroupingOption.CREATED_BY]: FormTypeColumnId.CreatedBy,
}

export function buildFormTypeFields({
  categoryOrder,
  rowActionHandlers,
}: FormTypeFieldsArgs): TableFields<IFormTypeRow> {
  const t = Localization.translator
  const appLabel = (value: string): string =>
    value === SHOWN_BAND_KEY ? t.listSections.shown : t.listSections.hidden
  const fields: readonly TableFieldDef<IFormTypeRow>[] = [
    {
      key: FormTypeColumnId.Icon,
      label: t.icon,
      value: row => row.iconName || '',
      searchable: false,
      column: {
        size: 56,
        maxSize: 56,
        enableSorting: false,
        meta: { align: 'center' },
        cell: record => {
          return (
            <span className="tw:flex tw:w-full tw:items-center tw:justify-center">
              <WorkflowTypeIcon iconName={record.iconName} />
            </span>
          )
        },
      },
      fixed: true,
    },
    {
      key: FormTypeColumnId.Name,
      label: t.formName,
      value: row => row.name,
      searchable: true,
      sortable: true,
      column: {
        size: 280,
        cell: record => {
          return (
            <span className="tw:flex tw:w-full tw:items-center tw:gap-2">
              <span className="tw:flex-1 tw:truncate" title={record.name}>
                {record.name || NO_VALUE}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="tw:shrink-0"
                onClick={e => {
                  e.stopPropagation()
                  rowActionHandlers.onEdit(record.id)
                }}
              >
                {t.edit_verb}
              </Button>
            </span>
          )
        },
      },
      fixed: true,
    },
    {
      key: FormTypeColumnId.Category,
      label: t.workflowCategory,
      value: row => normalizeUnassigned(row.categoryId),
      sortValue: row => {
        const categoryId = normalizeUnassigned(row.categoryId)
        return categoryId === EMPTY_OPTION ? categoryId : row.categoryName
      },
      searchText: row => row.categoryName,
      labelValue: row =>
        normalizeUnassigned(row.categoryId) === EMPTY_OPTION ||
        row.categoryId === UNASSIGNED
          ? t.unassigned
          : row.categoryName,
      bandOrder: () => [...categoryOrder, EMPTY_OPTION],
      searchable: true,
      facetable: true,
      groupable: true,
      sortable: true,
      column: {
        size: 220,
        cell: record => record.categoryName || NO_VALUE,
      },
    },
    {
      key: APP_FACET_KEY,
      label: t.app,
      value: row => (row.isEnabled ? SHOWN_BAND_KEY : HIDDEN_BAND_KEY),
      labelValue: row =>
        appLabel(row.isEnabled ? SHOWN_BAND_KEY : HIDDEN_BAND_KEY),
      bandOrder: () => [SHOWN_BAND_KEY, HIDDEN_BAND_KEY],
      searchable: false,
      facetable: true,
      groupable: true,
    },
    {
      key: FormTypeColumnId.ShowInApp,
      label: t.allowCreation,
      value: row => row.isEnabled,
      searchable: false,
      sortable: true,
      column: {
        header: () => (
          <ColumnHeaderInfo
            label={t.allowCreation}
            info={t.allowCreationInfo}
          />
        ),
        size: 160,
        cell: record => {
          return (
            <ShowInAppCell
              isEnabled={record.isEnabled}
              onChange={() => rowActionHandlers.onShowInApp(record.id)}
            />
          )
        },
      },
    },
    {
      key: FormTypeColumnId.AvailableTo,
      label: t.creationAccess,
      value: row => (row.isAdminOnly ? AUDIENCE_ADMIN_ONLY : AUDIENCE_EVERYONE),
      sortValue: row => (row.isAdminOnly ? t.adminsOnly : t.everyone),
      labelValue: row => (row.isAdminOnly ? t.adminsOnly : t.everyone),
      searchable: false,
      facetable: true,
      sortable: true,
      column: {
        header: () => (
          <ColumnHeaderInfo
            label={t.creationAccess}
            info={t.creationAccessInfo}
          />
        ),
        size: 160,
        cell: record => (record.isAdminOnly ? t.adminsOnly : t.everyone),
      },
    },
    {
      key: FormTypeColumnId.CreatedBy,
      label: t.createdBy,
      value: row => row.createdBy,
      sortValue: row => row.createdByCaption,
      labelValue: row => row.createdByCaption,
      bandOrder: () => [
        WorkflowTypeCreatedByEnum.DEFAULT,
        WorkflowTypeCreatedByEnum.PROJECT,
      ],
      searchable: false,
      facetable: true,
      groupable: true,
      sortable: true,
      column: {
        size: 160,
        cell: record => record.createdByCaption,
      },
    },
    {
      key: FormTypeColumnId.LastUpdated,
      label: t.lastUpdated,
      value: row => row.lastUpdatedSortValue,
      searchable: false,
      sortable: true,
      column: {
        size: 200,
        cell: record => record.lastUpdatedDisplay,
      },
    },
  ]

  return buildTableFields({
    getId: row => row.id,
    fields,
    bandLevels: [{ fieldKey: FormTypeColumnId.Category }],
    emptyOptionLabel: t.unassigned,
  })
}
